import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  Calendar, Clock, User, MapPin, Star, X, 
  CheckCircle, AlertCircle, ChevronLeft
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { toast } from 'sonner';

export default function PatientAppointments() {
  const { api } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewRatings, setReviewRatings] = useState({
    overall: 5,
    punctuality: 5,
    service: 5,
    infrastructure: 5,
    comment: ''
  });

  const fetchAppointments = useCallback(async () => {
    try {
      const response = await api.get('/patient/appointments');
      setAppointments(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const handleCancelAppointment = async () => {
    if (!selectedAppointment) return;

    try {
      await api.put(`/patient/appointments/${selectedAppointment.id}/cancel`, null, {
        params: { reason: cancelReason }
      });
      toast.success('Consulta cancelada com sucesso');
      setCancelDialogOpen(false);
      setCancelReason('');
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar consulta');
    }
  };

  const handleSubmitReview = async () => {
    if (!selectedAppointment) return;

    try {
      await api.post('/patient/reviews', {
        appointment_id: selectedAppointment.id,
        overall_rating: reviewRatings.overall,
        punctuality_rating: reviewRatings.punctuality,
        service_rating: reviewRatings.service,
        infrastructure_rating: reviewRatings.infrastructure,
        comment: reviewRatings.comment || null
      });
      toast.success('Avaliação enviada com sucesso');
      setReviewDialogOpen(false);
      setReviewRatings({ overall: 5, punctuality: 5, service: 5, infrastructure: 5, comment: '' });
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar avaliação');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Pendente', className: 'bg-amber-100 text-amber-700' },
      confirmed: { label: 'Confirmada', className: 'bg-emerald-100 text-emerald-700' },
      completed: { label: 'Realizada', className: 'bg-slate-100 text-slate-700' },
      cancelled: { label: 'Cancelada', className: 'bg-red-100 text-red-700' },
      no_show: { label: 'Não compareceu', className: 'bg-red-100 text-red-700' },
    };
    const { label, className } = statusMap[status] || statusMap.pending;
    return <Badge className={className}>{label}</Badge>;
  };

  const upcomingAppointments = appointments.filter(a => ['pending', 'confirmed'].includes(a.status));
  const pastAppointments = appointments.filter(a => ['completed', 'cancelled', 'no_show'].includes(a.status));

  const AppointmentCard = ({ appointment, showActions = true }) => (
    <div className="bg-white rounded-2xl border border-slate-100 p-6 hover:border-slate-200 transition-colors">
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <User className="w-8 h-8 text-slate-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {getStatusBadge(appointment.status)}
            {appointment.payment_type === 'health_plan' ? (
              <Badge variant="secondary">Convênio</Badge>
            ) : (
              <Badge variant="secondary">Particular</Badge>
            )}
          </div>
          <h3 className="font-semibold text-slate-900 text-lg">{appointment.doctor_name}</h3>
          <p className="text-slate-600">{appointment.specialty_name}</p>
          {appointment.provider_name && (
            <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
              <MapPin className="w-3 h-3" />
              {appointment.provider_name}
            </p>
          )}
          <div className="flex items-center gap-4 mt-3 text-sm text-slate-600">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4 text-primary" />
              {format(new Date(appointment.date), "dd 'de' MMMM", { locale: ptBR })}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4 text-primary" />
              {appointment.time_slot}
            </span>
          </div>
          {appointment.private_price && (
            <p className="text-sm font-medium text-primary mt-2">
              R$ {appointment.private_price.toFixed(2)}
            </p>
          )}
        </div>
      </div>

      {showActions && (
        <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
          {['pending', 'confirmed'].includes(appointment.status) && (
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 hover:bg-red-50"
              onClick={() => {
                setSelectedAppointment(appointment);
                setCancelDialogOpen(true);
              }}
              data-testid={`cancel-btn-${appointment.id}`}
            >
              <X className="w-4 h-4 mr-1" />
              Cancelar
            </Button>
          )}
          {appointment.status === 'completed' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedAppointment(appointment);
                setReviewDialogOpen(true);
              }}
              data-testid={`review-btn-${appointment.id}`}
            >
              <Star className="w-4 h-4 mr-1" />
              Avaliar
            </Button>
          )}
          <Link to={`/medico/${appointment.doctor_id}`}>
            <Button variant="ghost" size="sm">
              Ver perfil do médico
            </Button>
          </Link>
        </div>
      )}
    </div>
  );

  const StarRating = ({ value, onChange, label }) => (
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-600">{label}</span>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="p-1"
          >
            <Star
              className={`w-5 h-5 transition-colors ${
                star <= value ? 'text-amber-400 fill-amber-400' : 'text-slate-300'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="patient-appointments">
      <Navbar />

      <div className="container-app py-8">
        <Link to="/paciente" className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-6">
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </Link>

        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">Minhas Consultas</h1>

        <Tabs defaultValue="upcoming">
          <TabsList className="mb-6">
            <TabsTrigger value="upcoming" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Próximas ({upcomingAppointments.length})
            </TabsTrigger>
            <TabsTrigger value="past" className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Histórico ({pastAppointments.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse h-40 bg-white rounded-2xl"></div>
                ))}
              </div>
            ) : upcomingAppointments.length > 0 ? (
              <div className="space-y-4">
                {upcomingAppointments.map((appointment) => (
                  <AppointmentCard key={appointment.id} appointment={appointment} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
                <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Nenhuma consulta agendada</h3>
                <p className="text-slate-600 mb-6">Que tal agendar uma consulta agora?</p>
                <Link to="/buscar">
                  <Button className="bg-primary">Buscar Médico</Button>
                </Link>
              </div>
            )}
          </TabsContent>

          <TabsContent value="past">
            {pastAppointments.length > 0 ? (
              <div className="space-y-4">
                {pastAppointments.map((appointment) => (
                  <AppointmentCard key={appointment.id} appointment={appointment} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
                <CheckCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Sem histórico</h3>
                <p className="text-slate-600">Suas consultas realizadas aparecerão aqui</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar Consulta</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja cancelar esta consulta? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Motivo do cancelamento (opcional)"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
              Voltar
            </Button>
            <Button variant="destructive" onClick={handleCancelAppointment} data-testid="confirm-cancel-btn">
              Confirmar Cancelamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Avaliar Consulta</DialogTitle>
            <DialogDescription>
              Como foi sua experiência com {selectedAppointment?.doctor_name}?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <StarRating
              label="Avaliação Geral"
              value={reviewRatings.overall}
              onChange={(v) => setReviewRatings({ ...reviewRatings, overall: v })}
            />
            <StarRating
              label="Pontualidade"
              value={reviewRatings.punctuality}
              onChange={(v) => setReviewRatings({ ...reviewRatings, punctuality: v })}
            />
            <StarRating
              label="Atendimento"
              value={reviewRatings.service}
              onChange={(v) => setReviewRatings({ ...reviewRatings, service: v })}
            />
            <StarRating
              label="Infraestrutura"
              value={reviewRatings.infrastructure}
              onChange={(v) => setReviewRatings({ ...reviewRatings, infrastructure: v })}
            />
            <Textarea
              placeholder="Deixe um comentário (opcional)"
              value={reviewRatings.comment}
              onChange={(e) => setReviewRatings({ ...reviewRatings, comment: e.target.value })}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmitReview} className="bg-primary" data-testid="submit-review-btn">
              Enviar Avaliação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
